import { TreeViewCategory } from '@/types'
import { find, flatMapDeep } from 'lodash-es'

const getSelectableCategoryForSearchTerm = (
  categories: TreeViewCategory[] | undefined,
  categorySearchTerm: string | undefined
): TreeViewCategory | undefined => {
  if (!categories) {
    return undefined
  }
  if (categorySearchTerm) {
    // search term provided -> search for it in categories
    const foundCategory = findCategoryBySearchTerm(categories, categorySearchTerm)

    if (foundCategory?.isSelectable) {
      // category found and is selectabled -> return found category
      return foundCategory
    } else if (foundCategory && !foundCategory.isSelectable) {
      // category found but is not selectable -> search in children
      return getFirstSelectableCategory(foundCategory.children || [])
    } else if (!foundCategory) {
      // no category found for search term -> return first selectable category
      return getFirstSelectableCategory(categories)
    }
  } else {
    // no search term provided -> return first selectable category
    return getFirstSelectableCategory(categories)
  }
}

const getFirstSelectableCategory = (
  categories: TreeViewCategory[]
): TreeViewCategory | undefined => {
  return findFirstSelectableCategory(getCategoriesRootNode(categories))
}

const findFirstSelectableCategory = (category: TreeViewCategory): TreeViewCategory | undefined => {
  if (category.isSelectable) {
    return category
  } else if (category.children && category.children.length > 0) {
    let result: TreeViewCategory | undefined = undefined
    for (let i = 0; result === undefined && i < category.children.length; i++) {
      const child = category.children[i]
      if (child) {
        result = findFirstSelectableCategory(child)
      }
    }
    return result
  }
  return undefined
}

const findCategoryBySearchTerm = (
  categories: TreeViewCategory[],
  categorySearchTerm: string
): TreeViewCategory | undefined => {
  const getChildren = (
    category: TreeViewCategory
  ): (TreeViewCategory | TreeViewCategory[])[] | TreeViewCategory => {
    if (!category.children?.length) {
      return category
    }
    return [category, flatMapDeep(category.children, getChildren)]
  }

  return find(
    flatMapDeep(categories, getChildren),
    ({ searchTerm }) => searchTerm == categorySearchTerm
  )
}

const getOpenCategories = (
  categories: TreeViewCategory[],
  category: TreeViewCategory
): TreeViewCategory[] => {
  const mapToParent = new Map<number, TreeViewCategory>()

  const recurse = (arr: TreeViewCategory[], parent: TreeViewCategory) => {
    arr.forEach((cat) => {
      mapToParent.set(cat.id, parent)
      if (cat.children) {
        recurse(cat.children, cat)
      }
    })
  }

  recurse(categories, getCategoriesRootNode(categories))

  const openCategories: TreeViewCategory[] = [category]

  let parent = mapToParent.get(category.id)
  while (parent && parent.id != 0) {
    openCategories.push(parent)
    parent = mapToParent.get(parent.id)
  }

  return openCategories
}

const removeEmptyChildren = (categories: TreeViewCategory[]): TreeViewCategory[] => {
  const recurse = (cat: TreeViewCategory) => {
    if (cat.children) {
      if (cat.children.length === 0) {
        delete cat.children
      } else {
        cat.children.forEach((child) => {
          recurse(child)
        })
      }
    }
  }

  categories.forEach((category) => recurse(category))

  return categories
}

const getCategoriesRootNode = (categories: TreeViewCategory[]): TreeViewCategory => {
  return {
    id: 0,
    name: 'root',
    searchTerm: 'root',
    isSelectable: false,
    children: categories
  }
}

export {
  findCategoryBySearchTerm,
  getOpenCategories,
  getSelectableCategoryForSearchTerm,
  removeEmptyChildren
}
